import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AlertTriangle, ArrowLeft, CheckCircle2, CirclePlay, Play, Save, Trash2 } from '@/components/icons';
import {
  MAX_NOTIFY_EMAILS,
  NODE_TITLES,
  NOTIFY_CHANNEL_NAMES,
  validateFlow,
  type ConditionRule,
  type NotifyChannel,
  type NotifyWhen,
  type FlowNode,
  type FlowNodeType,
} from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { describeNode, fromCanvas, newId, stableJson, toCanvasEdges, toCanvasNodes, type CanvasEdge, type StepData } from '@/lib/flowView';
import { useIsDark } from '@/lib/useIsDark';
import { useRunCollection } from '@/lib/runCollection';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { useRunBuild } from '@/lib/runBuild';
import { RunBuildPicker } from '@/components/builds/RunBuildPicker';
import { useRunDevice } from '@/lib/runDevice';
import { RunDevicePicker } from '@/components/builds/RunDevicePicker';
import type { Flow, FlowNames } from '@/lib/types';
import { StepNode, StepShapeIcon, type StepNodeType } from '@/components/flows/StepNode';
import { Button, Card, CardBody, CardHeader, ErrorNotice, Field, Input, PageLoader, Select, Textarea, Toggle, useToast } from '@/components/ui';

const nodeTypes = { step: StepNode };

const PALETTE: { type: Exclude<FlowNodeType, 'start'>; hint: string }[] = [
  { type: 'test', hint: 'Run one test' },
  { type: 'group', hint: 'Run a test group' },
  { type: 'condition', hint: 'Go one way or the other' },
  { type: 'join', hint: 'Wait for branches' },
  { type: 'notify', hint: 'Send a message to Slack, Teams, Zoho Cliq or email' },
  { type: 'end', hint: 'Finish a path' },
];

const RULE_OPTIONS: { value: ConditionRule; label: string }[] = [
  { value: 'all_passed', label: 'Did everything pass?' },
  { value: 'any_failed', label: 'Did anything fail?' },
  { value: 'pass_rate_at_least', label: 'Is the pass rate at least…?' },
  { value: 'test_failed', label: 'Did a specific test fail?' },
];

/** First spot at or right of `want` that does not overlap an existing step (steps are 224 wide, up to 128 tall). */
function freePosition(taken: { x: number; y: number }[], want: { x: number; y: number }) {
  const pos = { ...want };
  while (taken.some((t) => Math.abs(t.x - pos.x) < 240 && Math.abs(t.y - pos.y) < 140)) pos.x += 260;
  return pos;
}

function freshNode(type: Exclude<FlowNodeType, 'start'>, position: { x: number; y: number }): FlowNode {
  const id = newId(type);
  switch (type) {
    case 'test':
      return { id, type, position, data: { testCaseId: '' } };
    case 'group':
      return { id, type, position, data: { groupId: '' } };
    case 'condition':
      return { id, type, position, data: { rule: 'all_passed', scope: 'previous' } };
    case 'join':
      return { id, type, position, data: { wait: 'all' } };
    case 'notify':
      return { id, type, position, data: { channels: [], emails: [], when: 'always', includeResults: true, scope: 'flow' } };
    case 'end':
      return { id, type, position, data: {} };
  }
}

export function FlowEditorPage() {
  const { flowId = '' } = useParams();
  const flow = useQuery({ queryKey: ['flow', flowId], queryFn: () => api.flow(flowId) });
  if (flow.isPending) return <PageLoader />;
  if (flow.isError) return <ErrorNotice error={flow.error} onRetry={() => flow.refetch()} />;
  return (
    <ReactFlowProvider>
      <Editor key={flow.data.id} flow={flow.data} />
    </ReactFlowProvider>
  );
}

function Editor({ flow }: { flow: Flow }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const dark = useIsDark();
  const { setCenter, getZoom } = useReactFlow();
  const { canModify, can } = useAuth();
  const project = useQuery({ queryKey: ['project', flow.projectId], queryFn: () => api.project(flow.projectId) });
  const orgId = project.data?.orgId;
  const editable = !!orgId && canModify('test.write', flow.createdById, orgId);
  const canRun = !!orgId && can('run.trigger', orgId);
  const runData = useRunCollection(flow.projectId);
  const runBuild = useRunBuild(flow.projectId, project.data?.platform);
  const runDevice = useRunDevice(flow.projectId, project.data?.platform);
  const tests = useQuery({ queryKey: ['tests', flow.projectId], queryFn: () => api.tests(flow.projectId) });
  const groups = useQuery({ queryKey: ['groups', flow.projectId], queryFn: () => api.groups(flow.projectId) });
  const channels = useQuery({ queryKey: ['notification-channels', flow.projectId], queryFn: () => api.notificationChannels(flow.projectId) });

  // Names of the project's tests/groups right now (falls back to the ones loaded with the flow).
  const names: FlowNames = useMemo(
    () => ({
      tests: tests.data ? Object.fromEntries(tests.data.map((t) => [t.id, t.name])) : flow.names.tests,
      groups: groups.data ? Object.fromEntries(groups.data.map((g) => [g.id, g.name])) : flow.names.groups,
    }),
    [tests.data, groups.data, flow.names],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<StepNodeType>(toCanvasNodes(flow.graph, flow.names) as StepNodeType[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(toCanvasEdges(flow.graph) as Edge[]);
  const [name, setName] = useState(flow.name);
  const [continueOnFailure, setContinueOnFailure] = useState(flow.continueOnFailure);
  // What was last saved; unsaved changes = the diagram, name or setting differs from it
  // (selecting or measuring steps changes the canvas state but not the diagram).
  const [saved, setSaved] = useState(() => stableJson({ name: flow.name, continueOnFailure: flow.continueOnFailure, graph: flow.graph }));

  const graph = useMemo(() => fromCanvas(nodes as unknown as Parameters<typeof fromCanvas>[0], edges as CanvasEdge[]), [nodes, edges]);
  const problems = useMemo(
    () =>
      validateFlow(
        graph,
        tests.data && groups.data ? { testIds: new Set(tests.data.map((t) => t.id)), groupIds: new Set(groups.data.map((g) => g.id)) } : undefined,
      ),
    [graph, tests.data, groups.data],
  );
  const problemNodes = useMemo(() => new Set(problems.map((p) => p.nodeId).filter(Boolean)), [problems]);

  const dirty = useMemo(() => stableJson({ name, continueOnFailure, graph }) !== saved, [name, continueOnFailure, graph, saved]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // What the canvas shows: fresh titles and problem markers on top of the editable state.
  const shown = useMemo(
    () => nodes.map((n) => ({ ...n, data: { ...n.data, ...describeNode(n.data.node, names), problem: problemNodes.has(n.id) } satisfies StepData })),
    [nodes, names, problemNodes],
  );

  const selectedNode = nodes.find((n) => n.selected);
  const selectedEdge = edges.find((e) => e.selected);

  const updateNode = useCallback(
    (id: string, change: (n: FlowNode) => FlowNode) =>
      setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, node: change(n.data.node) } } : n))),
    [setNodes],
  );

  const onConnect = useCallback(
    (c: Connection) => {
      const source = nodes.find((n) => n.id === c.source);
      const branch = source?.data.node.type === 'condition' && (c.sourceHandle === 'yes' || c.sourceHandle === 'no') ? c.sourceHandle : undefined;
      setEdges((es) =>
        addEdge({ ...c, id: newId('e'), ...(branch ? { label: branch === 'yes' ? 'Yes' : 'No', data: { branch } } : {}) }, es),
      );
    },
    [nodes, setEdges],
  );

  /** Adds a step below the selected one (connected to it) or below the lowest step. */
  const addStep = (type: Exclude<FlowNodeType, 'start'>) => {
    const from = selectedNode && selectedNode.data.node.type !== 'end' ? selectedNode : undefined;
    const lowest = nodes.reduce((y, n) => Math.max(y, n.position.y), 0);
    const position = freePosition(
      nodes.map((n) => n.position),
      from ? { x: from.position.x, y: from.position.y + 150 } : { x: nodes[0]?.position.x ?? 0, y: lowest + 150 },
    );
    const fresh = freshNode(type, position);
    // A new Notify step sends to every channel the project has connected; untick the ones not wanted.
    const node: FlowNode =
      fresh.type === 'notify' ? { ...fresh, data: { ...fresh.data, channels: (channels.data ?? []).map((c) => c.key as NotifyChannel) } } : fresh;
    setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { id: node.id, type: 'step', position, deletable: true, selected: true, data: { node, ...describeNode(node, names) } }]);
    // Keep the new step in view.
    void setCenter(position.x + 112, position.y + 40, { zoom: getZoom(), duration: 300 });
    // A Condition with both Yes and No already connected gets no extra connection.
    let branch: 'yes' | 'no' | undefined;
    if (from?.data.node.type === 'condition') {
      const used = new Set(edges.filter((e) => e.source === from.id).map((e) => e.sourceHandle));
      branch = !used.has('yes') ? 'yes' : !used.has('no') ? 'no' : undefined;
    }
    if (from && (from.data.node.type !== 'condition' || branch)) {
      setEdges((es) => [...es, { id: newId('e'), source: from.id, target: node.id, sourceHandle: branch ?? null, ...(branch ? { label: branch === 'yes' ? 'Yes' : 'No', data: { branch } } : {}) }]);
    }
  };

  const removeSelected = () => {
    if (selectedNode && selectedNode.data.node.type !== 'start') {
      setNodes((ns) => ns.filter((n) => n.id !== selectedNode.id));
      setEdges((es) => es.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    } else if (selectedEdge) {
      setEdges((es) => es.filter((e) => e.id !== selectedEdge.id));
    }
  };

  const save = useMutation({
    mutationFn: () => api.updateFlow(flow.id, { name: name.trim(), continueOnFailure, graph }),
    onSuccess: (result) => {
      setSaved(stableJson({ name: result.name, continueOnFailure: result.continueOnFailure, graph: result.graph }));
      qc.setQueryData(['flow', flow.id], result);
      qc.invalidateQueries({ queryKey: ['flows', flow.projectId] });
    },
  });
  const trySave = () =>
    save.mutate(undefined, {
      onSuccess: () => toast.success('Flow saved'),
      onError: (e) => toast.error(errorMessage(e)),
    });

  const run = useMutation({
    mutationFn: async () => {
      if (dirty && editable) await save.mutateAsync();
      return api.runFlow(flow.id, { collectionId: runData.collectionId, appBuildId: runBuild.appBuildId, deviceProfile: runDevice.deviceProfile });
    },
    onSuccess: (r) => {
      toast.success('Flow started');
      navigate(`/flow-runs/${r.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's' && editable) {
        e.preventDefault();
        trySave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const selectProblem = (nodeId?: string) => nodeId && setNodes((ns) => ns.map((n) => ({ ...n, selected: n.id === nodeId })));

  const ordered = [...shown].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);

  return (
    <div className="space-y-4">
      <Link to={`/projects/${flow.projectId}?tab=flows`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
        <ArrowLeft className="size-4" /> {project.data?.name ?? 'Project'}
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="flow-name" className="sr-only">
          Flow name
        </label>
        <input
          id="flow-name"
          value={name}
          readOnly={!editable}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          className="min-w-0 flex-1 rounded-xl bg-transparent px-2 py-1 -ml-2 text-2xl font-bold tracking-tight text-slate-900 focus:bg-white focus:ring-2 focus:ring-brand-500 focus:outline-none dark:text-white dark:focus:bg-slate-900"
        />
        {dirty && editable && <span className="text-sm text-amber-600">Unsaved changes</span>}
        {editable && (
          <Button variant="secondary" onClick={trySave} loading={save.isPending && !run.isPending} disabled={!dirty || !name.trim()} icon={<Save className="size-4" />}>
            Save
          </Button>
        )}
        {canRun && <RunBuildPicker state={runBuild} />}
        {canRun && <RunDevicePicker state={runDevice} />}
        {canRun && <RunCollectionPicker state={runData} />}
        {canRun && (
          <Button onClick={() => run.mutate()} loading={run.isPending} disabled={problems.length > 0} icon={<Play className="size-4" />} title={problems[0]?.message}>
            {dirty && editable ? 'Save & run' : 'Run flow'}
          </Button>
        )}
      </div>
      {!editable && orgId && (
        <p className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {flow.createdBy?.name ?? 'Someone else'} created this flow. You can run it; only they or a Project Admin can change it.
        </p>
      )}

      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          {editable && (
            <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Add a step">
              <span className="text-sm text-slate-500">Add:</span>
              {PALETTE.map((p) => (
                <Button key={p.type} size="sm" variant="outline" onClick={() => addStep(p.type)} title={p.hint} icon={<StepShapeIcon type={p.type} />}>
                  {NODE_TITLES[p.type]}
                </Button>
              ))}
              <span className="text-xs text-slate-400">Select a step first to add the next one below it, connected.</span>
            </div>
          )}
          <div className="h-[62vh] min-h-[480px] overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
            <ReactFlow
              nodes={shown}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodesDraggable={editable}
              nodesConnectable={editable}
              deleteKeyCode={editable ? ['Backspace', 'Delete'] : null}
              colorMode={dark ? 'dark' : 'light'}
              defaultEdgeOptions={{ type: 'smoothstep' }}
              fitView
              fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
              proOptions={{ hideAttribution: true }}
              aria-label="Flow diagram"
            >
              <Background gap={20} />
              <Controls showInteractive={false} />
            </ReactFlow>
          </div>
        </div>

        <div className="space-y-4">
          <StepSettings
            node={selectedNode?.data.node}
            edgeSelected={!!selectedEdge}
            editable={editable}
            names={names}
            tests={(tests.data ?? []).filter((t) => t.type !== 'PERFORMANCE')}
            groups={groups.data ?? []}
            channels={channels.data ?? []}
            onChange={(change) => selectedNode && updateNode(selectedNode.id, change)}
            onDelete={removeSelected}
          />
          <Card>
            <CardHeader title="Flow settings" />
            <CardBody>
              <div className="flex items-start gap-3">
                <Toggle checked={continueOnFailure} onChange={setContinueOnFailure} label="Continue after failures" disabled={!editable} />
                <div className="text-sm">
                  <div className="font-medium">Continue after failures</div>
                  <div className="text-xs text-slate-500">Off: after a failed test or group, only Condition steps continue the path. On: every connection is followed.</div>
                </div>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  {problems.length ? <AlertTriangle className="size-4 text-amber-500" /> : <CheckCircle2 className="size-4 text-emerald-500" />}
                  {problems.length ? `${problems.length} thing${problems.length === 1 ? '' : 's'} to fix before running` : 'Ready to run'}
                </span>
              }
            />
            {problems.length > 0 && (
              <ul className="space-y-1 px-5 pb-4 text-sm" aria-live="polite">
                {problems.slice(0, 12).map((p, i) => (
                  <li key={i}>
                    {p.nodeId ? (
                      <button type="button" className="text-left text-warning underline-offset-2 hover:underline" onClick={() => selectProblem(p.nodeId)}>
                        {p.message}
                      </button>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-300">{p.message}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader title="Steps as a list" description="The same flow in reading order, top to bottom." />
        <ol className="divide-y divide-slate-100 dark:divide-slate-800">
          {ordered.map((n) => {
            const next = edges
              .filter((e) => e.source === n.id)
              .map((e) => {
                const t = shown.find((x) => x.id === e.target);
                const label = (e.data as { branch?: string } | undefined)?.branch;
                return `${label ? `${label === 'yes' ? 'Yes' : 'No'}: ` : ''}${t?.data.title ?? '?'}${t && t.data.node.type !== 'start' && t.data.node.type !== 'end' ? ` (${t.data.detail})` : ''}`;
              });
            return (
              <li key={n.id} className="px-5 py-2.5 text-sm">
                <button type="button" className="text-left hover:text-primary" onClick={() => selectProblem(n.id)}>
                  <span className="font-medium">{n.data.title}</span> <span className="text-slate-500">- {n.data.detail}</span>
                </button>
                {next.length > 0 && <div className="text-xs text-slate-500">then {next.join(' · ')}</div>}
              </li>
            );
          })}
        </ol>
      </Card>
    </div>
  );
}

/** Settings of the selected step (or connection). */
function StepSettings({
  node,
  edgeSelected,
  editable,
  names,
  tests,
  groups,
  channels,
  onChange,
  onDelete,
}: {
  node?: FlowNode;
  edgeSelected: boolean;
  editable: boolean;
  names: FlowNames;
  tests: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  /** Notification channels the project has connected (Plugins page). */
  channels: { key: string; name: string; recipients?: string[] }[];
  onChange: (change: (n: FlowNode) => FlowNode) => void;
  onDelete: () => void;
}) {
  if (!node) {
    return (
      <Card>
        <CardHeader title={edgeSelected ? 'Connection' : 'Step settings'} description={edgeSelected ? 'Press Delete to remove it.' : 'Select a step on the diagram to change it.'} />
        {edgeSelected && editable && (
          <CardBody>
            <Button variant="outline" size="sm" onClick={onDelete} icon={<Trash2 className="size-4 text-rose-500" />}>
              Remove connection
            </Button>
          </CardBody>
        )}
      </Card>
    );
  }
  const { title, detail } = describeNode(node, names);
  const testOptions = [{ value: '', label: tests.length ? 'Choose a test…' : 'No tests in this project' }, ...tests.map((t) => ({ value: t.id, label: t.name }))];
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            {node.type === 'start' ? <CirclePlay className="size-4" /> : null}
            {title}
          </span>
        }
        description={detail}
      />
      <CardBody className="space-y-4">
        {node.type !== 'start' && (
          <Field label="Caption (optional)">
            {(id) => (
              <Input
                id={id}
                value={node.label ?? ''}
                disabled={!editable}
                maxLength={80}
                placeholder={NODE_TITLES[node.type]}
                onChange={(e) => onChange((n) => ({ ...n, label: e.target.value || undefined }))}
              />
            )}
          </Field>
        )}
        {node.type === 'test' && (
          <Field label="Test">
            {(id) => (
              <Select id={id} disabled={!editable} value={node.data.testCaseId} options={testOptions} onChange={(e) => onChange((n) => (n.type === 'test' ? { ...n, data: { testCaseId: e.target.value } } : n))} />
            )}
          </Field>
        )}
        {node.type === 'group' && (
          <Field label="Group" hint={groups.length ? undefined : 'Create groups on the project’s Groups tab.'}>
            {(id) => (
              <Select
                id={id}
                disabled={!editable}
                value={node.data.groupId}
                options={[{ value: '', label: groups.length ? 'Choose a group…' : 'No groups in this project' }, ...groups.map((g) => ({ value: g.id, label: g.name }))]}
                onChange={(e) => onChange((n) => (n.type === 'group' ? { ...n, data: { groupId: e.target.value } } : n))}
              />
            )}
          </Field>
        )}
        {node.type === 'condition' && (
          <>
            <Field label="Question" hint="Connect the green Yes and red No handles to the next steps.">
              {(id) => (
                <Select
                  id={id}
                  disabled={!editable}
                  value={node.data.rule}
                  options={RULE_OPTIONS}
                  onChange={(e) =>
                    onChange((n) =>
                      n.type === 'condition'
                        ? { ...n, data: { ...n.data, rule: e.target.value as ConditionRule, threshold: e.target.value === 'pass_rate_at_least' ? (n.data.threshold ?? 80) : n.data.threshold } }
                        : n,
                    )
                  }
                />
              )}
            </Field>
            {node.data.rule !== 'test_failed' && (
              <Field label="Look at">
                {(id) => (
                  <Select
                    id={id}
                    disabled={!editable}
                    value={node.data.scope}
                    options={[
                      { value: 'previous', label: 'The step just before' },
                      { value: 'flow', label: 'Everything in the flow so far' },
                    ]}
                    onChange={(e) => onChange((n) => (n.type === 'condition' ? { ...n, data: { ...n.data, scope: e.target.value as 'previous' | 'flow' } } : n))}
                  />
                )}
              </Field>
            )}
            {node.data.rule === 'pass_rate_at_least' && (
              <Field label="Pass rate (%)">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={1}
                    max={100}
                    disabled={!editable}
                    value={node.data.threshold ?? 80}
                    onChange={(e) => onChange((n) => (n.type === 'condition' ? { ...n, data: { ...n.data, threshold: Math.min(100, Math.max(1, Number(e.target.value) || 1)) } } : n))}
                  />
                )}
              </Field>
            )}
            {node.data.rule === 'test_failed' && (
              <Field label="Test">
                {(id) => (
                  <Select
                    id={id}
                    disabled={!editable}
                    value={node.data.testCaseId ?? ''}
                    options={testOptions}
                    onChange={(e) => onChange((n) => (n.type === 'condition' ? { ...n, data: { ...n.data, testCaseId: e.target.value || undefined } } : n))}
                  />
                )}
              </Field>
            )}
          </>
        )}
        {node.type === 'join' && (
          <Field label="Continue when">
            {(id) => (
              <Select
                id={id}
                disabled={!editable}
                value={node.data.wait}
                options={[
                  { value: 'all', label: 'All incoming branches have finished' },
                  { value: 'any', label: 'The first branch has finished' },
                ]}
                onChange={(e) => onChange((n) => (n.type === 'join' ? { ...n, data: { wait: e.target.value as 'all' | 'any' } } : n))}
              />
            )}
          </Field>
        )}
        {node.type === 'notify' && (
          <NotifySettings key={node.id} node={node} editable={editable} channels={channels} onChange={onChange} />
        )}
        {editable && node.type !== 'start' && (
          <Button variant="outline" size="sm" onClick={onDelete} icon={<Trash2 className="size-4 text-rose-500" />}>
            Remove step
          </Button>
        )}
      </CardBody>
    </Card>
  );
}

const WHEN_OPTIONS: { value: NotifyWhen; label: string }[] = [
  { value: 'always', label: 'Always' },
  { value: 'failed', label: 'Only if something failed' },
  { value: 'passed', label: 'Only if everything passed' },
];

/** Email addresses typed as text: commas, semicolons, spaces or new lines between them. */
const parseEmails = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean)
    .slice(0, MAX_NOTIFY_EMAILS);

/** A Notify step: where it sends, to whom, when, and what it says. */
function NotifySettings({
  node,
  editable,
  channels,
  onChange,
}: {
  node: Extract<FlowNode, { type: 'notify' }>;
  editable: boolean;
  channels: { key: string; name: string; recipients?: string[] }[];
  onChange: (change: (n: FlowNode) => FlowNode) => void;
}) {
  const d = node.data;
  const set = (patch: Partial<typeof d>) => onChange((n) => (n.type === 'notify' ? { ...n, data: { ...n.data, ...patch } } : n));
  // The addresses as typed (kept apart so a half-typed address or a trailing comma isn't lost).
  const [emailText, setEmailText] = useState(d.emails.join(', '));
  // Connected channels first, then any the step names that the project no longer has.
  const listed = [
    ...channels.map((c) => ({ key: c.key as NotifyChannel, name: c.name, connected: true })),
    ...d.channels.filter((k) => !channels.some((c) => c.key === k)).map((k) => ({ key: k, name: NOTIFY_CHANNEL_NAMES[k], connected: false })),
  ];
  const emailDefaults = channels.find((c) => c.key === 'email')?.recipients ?? [];
  return (
    <>
      <fieldset className="space-y-2">
        <legend className="mb-2 block text-xs font-medium tracking-label text-fg-secondary uppercase">Send to</legend>
        {listed.length === 0 ? (
          <p className="text-xs text-fg-muted">
            No notification channels are connected to this project yet. A Project Admin connects Slack, Microsoft Teams, Zoho Cliq or email on the Plugins page.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
            {listed.map((c) => (
              <li key={c.key}>
                <label className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={d.channels.includes(c.key)}
                    disabled={!editable}
                    onChange={(e) => set({ channels: e.target.checked ? [...d.channels, c.key] : d.channels.filter((k) => k !== c.key) })}
                  />
                  <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                  {!c.connected && <span className="text-xs text-warning">Not connected</span>}
                </label>
              </li>
            ))}
          </ul>
        )}
      </fieldset>
      {d.channels.includes('email') && (
        <Field
          label="Email recipients"
          hint={
            emailDefaults.length
              ? `Separate addresses with commas. Leave empty to use the email plugin’s recipients: ${emailDefaults.join(', ')}.`
              : 'Separate addresses with commas. Leave empty to use the email plugin’s recipients.'
          }
        >
          {(id) => (
            <Textarea
              id={id}
              className="min-h-16"
              disabled={!editable}
              value={emailText}
              placeholder={emailDefaults.join(', ') || 'qa-team@example.com, lead@example.com'}
              onChange={(e) => {
                setEmailText(e.target.value);
                set({ emails: parseEmails(e.target.value) });
              }}
            />
          )}
        </Field>
      )}
      <Field label="Send when" hint={d.scope === 'flow' ? 'Judged on everything in the flow so far.' : 'Judged on the step just before.'}>
        {(id) => <Select id={id} disabled={!editable} value={d.when} options={WHEN_OPTIONS} onChange={(e) => set({ when: e.target.value as NotifyWhen })} />}
      </Field>
      <div className="flex items-start gap-3">
        <Toggle checked={d.includeResults} onChange={(on) => set({ includeResults: on })} label="Include the results" disabled={!editable} />
        <div className="text-sm">
          <div className="font-medium">Include the results</div>
          <div className="text-xs text-slate-500">How many tests passed and failed, the names of failed tests, and a link to the flow run.</div>
        </div>
      </div>
      <Field label="Results of">
        {(id) => (
          <Select
            id={id}
            disabled={!editable}
            value={d.scope}
            options={[
              { value: 'flow', label: 'Everything in the flow so far' },
              { value: 'previous', label: 'The step just before' },
            ]}
            onChange={(e) => set({ scope: e.target.value as 'previous' | 'flow' })}
          />
        )}
      </Field>
      <Field label="Title (optional)" hint="The chat card’s title and the email subject. Empty: the flow’s name and its result.">
        {(id) => <Input id={id} disabled={!editable} maxLength={150} value={d.subject ?? ''} onChange={(e) => set({ subject: e.target.value || undefined })} />}
      </Field>
      <Field label="Message (optional)" hint="Your own text, shown above the results.">
        {(id) => (
          <Textarea
            id={id}
            disabled={!editable}
            maxLength={2000}
            value={d.message ?? ''}
            placeholder="e.g. Release candidate checked on staging. Please review before 5 pm."
            onChange={(e) => set({ message: e.target.value || undefined })}
          />
        )}
      </Field>
    </>
  );
}
